"use client"

import React, { useState, useEffect, useCallback } from 'react'
import { usePathname } from 'next/navigation'
import { AffiliatePicksCard } from "@/components/tools/affiliate-picks-card"
import { contentAnalytics, pageFamilyFromPath } from "@/lib/analytics"
import { DirectusProduct } from "@/lib/directus"
import { withAmazonTag } from "@/lib/affiliate"

/** Join key for every event this rail emits (impression, per-product impression, clicks). */
const PICKS_RAIL_SURFACE = "blog-picks-rail"

interface LazyProductRecommendationsProps {
  category: string
  limit?: number
  /** Optional article slug for tracking (will extract from pathname if not provided) */
  articleSlug?: string
  /** Optional article tags for keyword-based product scoring */
  tags?: string[]
}

export function LazyProductRecommendations({
  category,
  limit = 3,
  articleSlug,
  tags
}: LazyProductRecommendationsProps) {
  const [products, setProducts] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)
  const pathname = usePathname()

  // Extract slug from pathname if not provided
  const effectiveSlug = articleSlug || pathname?.split('/').pop() || 'unknown'

  const pageFamily = pageFamilyFromPath(pathname)

  // Stamped on the container impression, the per-product impressions, and every
  // click, so the rail's CTR can be read per post and per route family. Until
  // 2026-09-13 neither side carried the slug.
  const railEventProps = { article_slug: effectiveSlug, page_family: pageFamily }

  // Track product click
  const handleProductClick = useCallback((product: DirectusProduct) => {
    contentAnalytics.productRecommendationClicked(
      effectiveSlug,
      product.name,
      product.affiliate_url || '',
      { surface: PICKS_RAIL_SURFACE, page_family: pageFamily },
    )
  }, [effectiveSlug, pageFamily])

  useEffect(() => {
    let isMounted = true
    
    const loadProducts = async () => {
      try {
        setIsLoading(true)
        setHasError(false)
        
        const params = new URLSearchParams({ category, limit: String(limit) })
        if (effectiveSlug && effectiveSlug !== 'unknown') params.set('slug', effectiveSlug)
        if (tags && tags.length > 0) params.set('tags', tags.join(','))

        const response = await fetch(
          `/api/blog/product-recommendations?${params.toString()}`
        )
        
        if (!response.ok) {
          throw new Error('Failed to fetch products')
        }
        
        const data = await response.json()
        
        // Only update state if component is still mounted
        if (isMounted) {
          setProducts((data.products || []).map((p: DirectusProduct) => ({ ...p, affiliate_url: withAmazonTag(p.affiliate_url || '', 'rail') })))
        }
      } catch (error) {
        console.error('Error loading product recommendations:', error)
        if (isMounted) {
          setHasError(true)
        }
      } finally {
        if (isMounted) {
          setIsLoading(false)
        }
      }
    }

    // Reduced delay for faster loading
    const timer = setTimeout(loadProducts, 100)
    return () => {
      clearTimeout(timer)
      isMounted = false
    }
  }, [category, limit, effectiveSlug, tags])

  // No loading placeholder: the old ~150px spinner card mounted on every post
  // and then swapped or vanished, an unconditional layout shift in the band
  // where Mediavine injects footer ads (CWV feeds their RPM). Rendering nothing
  // until products resolve shifts once, early, and only when there IS content.
  if (isLoading || hasError || products.length === 0) {
    return null
  }

  // Unified "Our picks" affiliate card — the Claude Design standard for full-width
  // 3-product blocks, shared with the tool calculators so every affiliate surface
  // reads as one card. onProductClick preserves the existing blog click telemetry.
  return (
    <div className="mb-8 md:mb-12">
      <AffiliatePicksCard
        title="Recommended Products"
        description={effectiveSlug === 'does-grass-seed-expire'
          ? 'Grass seed and supplies for the planting job after you check the bag.'
          : `Tools and supplies to help you implement these ${category
            .toLowerCase()
            .replace(/\s*tips\s*$/i, '')} tips`}
        products={products}
        onProductClick={handleProductClick}
        surface={PICKS_RAIL_SURFACE}
        eventProps={railEventProps}
        productImpressions
      />
    </div>
  )
}
